import { z } from "zod";
import { ShiftingType } from "../enums/ShiftingType.js";
import { WorkingHoursSchema } from "../planning/planning.types.js";
import { IbanSchema } from "./iban.js";
/** Coerce to number but preserve null/undefined/NaN (unlike z.coerce.number which turns null into 0) */
const coerceNumberOrNull = z.preprocess((val) => {
    if (val === null || val === undefined) {
        return val;
    }
    const num = Number(val);
    return Number.isNaN(num) ? null : num;
}, z.number().nullable().optional());
export const CompanyRoleArray = ["manager", "worker"];
export const FunctionalRoleArray = [...CompanyRoleArray, "accountant"];
export const OrderKindArray = ["monthly", "yearly", "sms"];
/** Billing cycle — source of truth for the company's plan (monthly or yearly). */
export const BillingCycleArray = ["monthly", "yearly"];
export const OrderStateArray = [
    "PENDING",
    "PROCESSING",
    "AUTHORISED",
    "COMPLETED",
    "CANCELLED",
    //"FAILED", // -> PENDING orders can be retried - Revolut reverts failed payments to pending state
];
export const CompanyTypeArray = [
    "EI",
    "EIRL",
    "EURL",
    "SARL",
    "SAS",
    "SASU",
    "SA",
    "SNC",
    "SCS",
    "SCA",
];
/** Company types that require declaring a capital social (capital social obligatoire). */
export const CompanyTypesWithCapital = new Set([
    "EURL",
    "SARL",
    "SAS",
    "SASU",
    "SA",
    "SNC",
    "SCS",
    "SCA",
]);
export const UpdateAccountSettingPayloadSchema = z.object({
    lastName: z.string(),
    firstName: z.string(),
    email: z.string().email(),
});
export const ApeCodeSchema = z
    .string()
    .trim()
    .regex(/^(?:[0-9]{2}\.?[0-9]{2}[a-zA-Z])?$/, {
    message: "Le code APE doit contenir 4 chiffres et une lettre (ex. 6201Z ou 62.01Z)",
})
    .transform((value) => value.replace(".", "").toUpperCase());
export const RequiredApeCodeSchema = ApeCodeSchema.refine((value) => value.length > 0, {
    message: "Champ obligatoire",
});
export const UpdateInvoicingStep1PayloadSchema = z
    .object({
    legalName: z.string(),
    siret: z.string(),
    apeCode: ApeCodeSchema,
    apeLabel: z.string(),
    companyType: z.enum(CompanyTypeArray),
    companyCapital: coerceNumberOrNull,
})
    .strict();
export const UpdateInvoicingStep2PayloadSchema = z
    .object({
    legalAddress: z.string(),
    legalAddress2: z.string().optional(),
    legalCity: z.string(),
    legalZip: z.string(),
})
    .strict();
export const UpdateInvoicingStep3PayloadSchema = z
    .object({
    shiftingType: z.nativeEnum(ShiftingType),
    shiftingAmount: coerceNumberOrNull,
    shiftingZoneData: z.string().optional(),
})
    .strict();
export const UpdateInvoicingStep4PayloadSchema = z
    .object({
    invoiceFirstNumber: coerceNumberOrNull,
    invoicePrefix: z.string().optional(),
})
    .strict();
export const UpdateInvoicingStep5PayloadSchema = z
    .object({
    logoContentType: z.string().optional(),
    logo: z.any().optional(),
})
    .strict();
/**
 * `z.union` retient le PREMIER schéma qui valide — chaque step ci-dessus est `.strict()`
 * (#1213 : Step4/Step5, tout-optionnels, avalaient sinon silencieusement les champs de
 * l'autre sous le mode `strip` par défaut de Zod). Un payload ne valide donc plus que
 * contre le step dont il satisfait exactement les clés (ni manquante, ni étrangère) ;
 * un payload ambigu (mélange de deux steps, ou clé inconnue) ne matche plus rien et fait
 * échouer `.parse()` — géré en amont par `ZodValidationPipe` (400), jamais un 500 non catché.
 */
export const UpdateInvoicingPartialPayloadSchema = z.union([
    UpdateInvoicingStep1PayloadSchema,
    UpdateInvoicingStep2PayloadSchema,
    UpdateInvoicingStep3PayloadSchema,
    UpdateInvoicingStep4PayloadSchema,
    UpdateInvoicingStep5PayloadSchema,
]);
export const UpdateInvoicingPayloadSchema = z.object({
    legalName: z.string(),
    legalAddress: z.string(),
    legalAddress2: z.string().optional(),
    legalCity: z.string(),
    legalZip: z.string(),
    legalLatitude: coerceNumberOrNull,
    legalLongitude: coerceNumberOrNull,
    legalScore: coerceNumberOrNull,
    siret: z.string(),
    companyType: z.enum(CompanyTypeArray),
    companyCapital: coerceNumberOrNull,
    apeCode: ApeCodeSchema,
    apeLabel: z.string(),
    rcsNumber: z.string().optional(),
    vatId: z.string().optional(),
    emailFooter: z.string().optional(),
    iban: IbanSchema.optional(),
    deadlineToPay: coerceNumberOrNull,
    latePayment: z.string().optional(),
    invoiceFirstNumber: coerceNumberOrNull,
    invoicePrefix: z.string().optional(),
    shiftingLabel: z.string().optional(),
    shiftingAmount: coerceNumberOrNull,
    shiftingType: z.nativeEnum(ShiftingType),
    shiftingZoneData: z.string().optional(),
    color: z.string().optional(),
    color2: z.string().optional(),
    logoContentType: z.string().optional(),
    logo: z.any().optional(),
});
export const CURRENT_CGU_VERSION = 2;
export const AcceptCguPayloadSchema = z.object({ version: z.number().int().positive() });
// ==================== Company Settings — Granular Payloads ====================
// --- Billing: Travel Fees ---
export const UpdateTravelFeesPayloadSchema = z.object({
    shiftingType: z.nativeEnum(ShiftingType),
    shiftingLabel: z.string().optional(),
    shiftingAmount: coerceNumberOrNull,
    shiftingZoneData: z.string().optional(),
});
// --- Billing: Legal Identity ---
export const UpdateLegalIdentityPayloadSchema = z.object({
    commercialName: z.string().optional(),
    legalName: z.string(),
    companyType: z.enum(CompanyTypeArray),
    companyCapital: coerceNumberOrNull,
});
// --- Billing: Legal Headquarters ---
export const UpdateLegalHeadquartersPayloadSchema = z.object({
    legalAddress: z.string(),
    legalAddress2: z.string().optional(),
    legalCity: z.string(),
    legalZip: z.string(),
});
// --- Billing: Legal Numbers ---
export const UpdateLegalNumbersPayloadSchema = z.object({
    siret: z.string(),
    apeCode: ApeCodeSchema,
    apeLabel: z.string(),
    rcsNumber: z.string().optional(),
    vatId: z.string().optional(),
});
// --- Billing: Invoice Appearance ---
export const UpdateInvoiceAppearancePayloadSchema = z.object({
    logo: z.any().optional(),
    logoContentType: z.string().optional(),
    color: z.string().optional(),
    color2: z.string().optional(),
});
// --- Billing: Invoice Parameters ---
export const UpdateInvoiceParametersPayloadSchema = z.object({
    iban: IbanSchema.optional(),
    deadlineToPay: coerceNumberOrNull,
    latePayment: z.string().optional(),
    invoiceFirstNumber: coerceNumberOrNull,
    invoicePrefix: z.string().optional(),
});
// --- My Business: Descriptor ---
export const JobCategoryArray = [
    "Soins animaliers",
    "Soin humain",
    "Entretien",
    "Dépannage",
    "Autre",
];
/**
 * Métiers proposés par catégorie (`BusinessDescriptorTab.tsx`, formulaire vitrine v2).
 * "Autre" termine chaque liste non vide ; la catégorie "Autre" elle-même n'a pas
 * de liste — seul le jobTitle littéral "Autre" lui est valide.
 */
export const JOBS_BY_CATEGORY = {
    "Soins animaliers": [
        "Maréchal-ferrant",
        "Podologue équin",
        "Ostéopathe animalier",
        "Technicien dentaire équin",
        "Shiatsu animalier",
        "Massage animalier",
        "Éducateur canin",
        "Autre",
    ],
    "Soin humain": [
        "Ostéopathe à domicile",
        "Coach sportif à domicile",
        "Diététicien à domicile",
        "Service à la personne",
        "Coiffeur à domicile",
        "Autre",
    ],
    Entretien: ["Ramoneur", "3D", "Nettoyage", "Jardinier à domicile", "Autre"],
    Dépannage: ["Électroménager", "Serrurier", "Autre"],
    Autre: [],
};
export function isJobTitleValidForCategory(jobCategory, jobTitle) {
    const jobs = JOBS_BY_CATEGORY[jobCategory];
    return jobs.length === 0 ? jobTitle === "Autre" : jobs.includes(jobTitle);
}
// --- Registration (public) ---
// jobCategory doit être déclaré avant ce schéma : `z.enum(JobCategoryArray)`
// s'exécute au chargement du module, avant JobCategoryArray serait une TDZ.
/**
 * Formulaire d'inscription vitrine v2 (`POST /api/registration/v2/:email`, #1316) —
 * endpoint neuf, `.strict()` : un client qui envoie `companyType`/adresse/`siret`
 * (champs v1) prend un 400 plutôt qu'un strip silencieux. `jobCategory`/`jobTitle`
 * sont requis dès l'inscription et persistés dans `company.features` ; `jobTitle`
 * doit appartenir aux métiers de `jobCategory` (`isJobTitleValidForCategory`).
 * Les 7 champs UTM suivent la même troncature silencieuse côté service que v1
 * (#1327/#1328) — pas de `.max()` bloquant ici. Même posture pour fbc/fbp/eventId/
 * marketingConsent (#1329) : pas de `.max()`, la troncature vit dans `buildAcquisition()`.
 */
export const CompanyRegistrationV2PayloadSchema = z
    .object({
    // Bornes alignées sur les colonnes cibles (user.first_name/last_name varchar(100),
    // company.phone varchar(50)) — un payload public non authentifié ne doit pas dépendre
    // du driver DB pour rejeter une valeur trop longue.
    firstName: z.string().trim().min(1).max(100),
    lastName: z.string().trim().max(100).optional(),
    phone: z.string().trim().min(1).max(50),
    company: z.string().trim().min(1).max(255),
    jobCategory: z.enum(JobCategoryArray),
    jobTitle: z.string().trim().min(1).max(100),
    utmSource: z.string().optional(),
    utmMedium: z.string().optional(),
    utmCampaign: z.string().optional(),
    utmContent: z.string().optional(),
    utmTerm: z.string().optional(),
    landingPage: z.string().optional(),
    referrer: z.string().optional(),
    fbc: z.string().optional(),
    fbp: z.string().optional(),
    // "true"/"false" en string, jamais un booléen — même contrat que le DTO v1 (#1329),
    // converti au seam service par `parseMarketingConsent()`.
    marketingConsent: z.string().optional(),
    eventId: z.string().optional(),
})
    .strict()
    .superRefine((data, ctx) => {
    if (!isJobTitleValidForCategory(data.jobCategory, data.jobTitle)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["jobTitle"],
            message: `jobTitle "${data.jobTitle}" is not valid for jobCategory "${data.jobCategory}"`,
        });
    }
});
// --- My Business: Descriptor ---
export const UpdateBusinessDescriptorPayloadSchema = z.object({
    // Optional so a partial update (e.g. editing only `businessDescription` from the
    // RDV settings tab) does not have to resend — and risk reverting — the category.
    // The backend applies each field only when present (`!== undefined`).
    jobCategory: z.enum(JobCategoryArray).nullable().optional(),
    jobTitle: z.string().optional(),
    commercialName: z.string().optional(),
    logo: z.any().optional(),
    logoContentType: z.string().optional(),
    businessDescription: z.string().optional(),
    // Public showcase job label (#564). Optional partial-update field; the backend
    // applies it only when present, and `"" → null` means "fall back to jobTitle".
    publicJobLabel: z.string().optional(),
});
// --- My Business: Organization ---
// WorkingHours format aligned with planning ADR-2 (ISO 8601 day keys, { start, end })
export const UpdateOrganizationPayloadSchema = z.object({
    workingHours: WorkingHoursSchema.optional(),
    maxDistanceFromHome: coerceNumberOrNull,
    maxDistanceBetweenEvents: coerceNumberOrNull,
    maxTimeBetweenEvents: coerceNumberOrNull,
    excludedZones: z.array(z.string()).optional(),
});
// --- Notifications ---
export const UpdateNotificationsPayloadSchema = z.object({
    /**
     * Email template fields. Three semantics for each:
     *   - omitted: do not touch the column
     *   - `null`: reset to default (mail builder will use the default template)
     *   - string (including empty ""): store verbatim
     */
    reminderEmailTemplate: z.string().nullable().optional(),
    creationEmailTemplate: z.string().nullable().optional(),
    updateEmailTemplate: z.string().nullable().optional(),
    invoiceEmailTemplate: z.string().nullable().optional(),
    invoiceReminderEmailTemplate: z.string().nullable().optional(),
    creditNoteEmailTemplate: z.string().nullable().optional(),
    scheduledReminderEmailTemplate: z.string().nullable().optional(),
    emailSignature: z.string().nullable().optional(),
    notificationAtEventSave: z.boolean().optional(),
    notificationRemind: z.boolean().optional(),
    notificationSmsRemind: z.boolean().optional(),
    customerEmailCopyEnabled: z.boolean().optional(),
});
export const SubscriptionStatusPayloadSchema = z.object({
    enablePayment: z.boolean(),
    billingCycleAnchor: z.coerce.date().nullable(),
    billingCycle: z.enum(BillingCycleArray),
    paymentNeeded: z.boolean(),
});
/**
 * Discriminates the payment scenario for the frontend modal routing.
 *
 * - `endOfTrial` — first subscription, no order created yet.
 * - `recurringFail` — recurring payment failed (covers both retryable pending
 *   orders and the case where the cron has given up after 3+ attempts; the
 *   `RevolutModal` reads `existingOrderId` to choose retry vs new order).
 * - `smsUsageFail` — SMS consumption order pending payment.
 * - `none` — fallback when no payment is needed (sentinel: should never be
 *   read by the frontend, which mounts the modal only when `paymentNeeded`).
 */
export const PaymentReasonArray = ["endOfTrial", "recurringFail", "smsUsageFail", "none"];
export const PaymentStatusPayloadSchema = z.object({
    reason: z.enum(PaymentReasonArray),
    blocking: z.boolean(),
    amount: z.number(),
    orderKind: z.enum(OrderKindArray),
    existingOrderId: z.string().nullable(),
    subscriptionAmount: z.number(),
    /** Resolved monthly subscription price for this company (centimes). */
    monthlyAmount: z.number(),
    /** Resolved yearly subscription price for this company (centimes). */
    yearlyAmount: z.number(),
    smsAmount: z.number(),
    smsCount: z.number(),
    smsUnitPrice: z.number().nullable(),
    mode: z.enum(["prod", "sandbox", "dev"]),
});
//# sourceMappingURL=types.js.map